/**
 * Media normalization utilities
 * Handles different media structures that can exist in posts
 */

import { logger } from './logger';

export interface NormalizedMedia {
  url: string;
  type: 'image' | 'video';
  fileName?: string;
  uploadedAt?: string;
  fileSize?: number;
  thumbnailUrl?: string;
  mimeType?: string;
}

/**
 * Normalizes media from various possible structures to a consistent format
 */
export function normalizeMedia(media: any): NormalizedMedia | null {
  if (!media) {
    return null;
  }

  // Debug log the media structure (only in development)


  // Handle array of media (new multi-media support)
  if (Array.isArray(media)) {
    if (media.length === 0) return null;
    // Take the first media item for now (can be extended for multi-media later)
    const firstMedia = media[0];

    return normalizeMedia(firstMedia);
  }

  // Handle string URL directly
  if (typeof media === 'string') {
    if (media === '[object Object]') {
      logger.debug('mediaUtils', 'Invalid object serialization detected');
      return null; // Invalid serialization
    }
    if (media.trim() === '') {
      return null; // Empty string should be treated as no media
    }
    return {
      url: media,
      type: inferMediaTypeFromUrl(media)
    };
  }

  // Handle Firestore mapValue structure FIRST (before checking for object.url)
  if (typeof media === 'object' && media !== null && media.mapValue && media.mapValue.fields) {
    const fields = media.mapValue.fields;
    const url = fields.url?.stringValue || '';
    const rawType = fields.type?.stringValue || 'image';
    const normalizedType = normalizeMediaType(rawType);
    

    
    return {
      url: url,
      type: normalizedType,
      fileName: fields.fileName?.stringValue,
      uploadedAt: fields.uploadedAt?.stringValue,
      fileSize: fields.fileSize?.integerValue ? parseInt(fields.fileSize.integerValue) : undefined,
      thumbnailUrl: fields.thumbnailUrl?.stringValue,
      mimeType: fields.mimeType?.stringValue
    };
  }

  // Handle standard object structure
  if (typeof media === 'object' && media !== null && media.url) {
    const rawType = media.type || 'image';
    let normalizedType = normalizeMediaType(rawType);
    
    // Enhanced type detection: prioritize URL-based detection for video files
    const urlBasedType = inferMediaTypeFromUrl(media.url);
    if (urlBasedType === 'video' && normalizedType === 'image') {
      normalizedType = 'video';

    }
    

    
    return {
      url: media.url,
      type: normalizedType,
      fileName: media.fileName,
      uploadedAt: media.uploadedAt,
      fileSize: media.fileSize,
      thumbnailUrl: media.thumbnailUrl,
      mimeType: media.mimeType
    };
  }

  // Handle objects without direct URL property (might have nested structure)
  if (typeof media === 'object' && media !== null) {
    // Check for nested URL properties
    if (media.fields?.url?.stringValue) {
      return {
        url: media.fields.url.stringValue,
        type: normalizeMediaType(media.fields.type?.stringValue || 'image')
      };
    }
  }

  logger.debug('mediaUtils', 'No matching media structure found');
  return null;
}

/**
 * Normalizes media type to standard format
 */
function normalizeMediaType(type: string): 'image' | 'video' {
  if (!type) return 'image';
  
  const normalizedType = type.toLowerCase().trim();
  
  // Handle various video type formats (both lowercase and uppercase)
  if (normalizedType === 'video' || 
      type.toUpperCase() === 'VIDEO' || 
      normalizedType.includes('video') ||
      normalizedType.includes('mp4') ||
      normalizedType.includes('mov') ||
      normalizedType.includes('avi') ||
      normalizedType.includes('webm')) {
    return 'video';
  }
  
  return 'image'; // Default to image
}

/**
 * Infers media type from URL extension
 */
function inferMediaTypeFromUrl(url: string): 'image' | 'video' {
  const videoExtensions = ['.mp4', '.mov', '.avi', '.webm', '.m4v'];
  const lowerUrl = url.toLowerCase();
  
  for (const ext of videoExtensions) {
    if (lowerUrl.includes(ext)) return 'video';
  }
  
  return 'image';
}

/**
 * Gets media format emoji for display
 */
export function getMediaFormatEmoji(media: any): string {
  const normalized = normalizeMedia(media);
  
  if (!normalized) return '📝'; // Text only
  if (normalized.type === 'video') return '🎬'; // Video
  if (normalized.type === 'image') return '🖼️'; // Image
  
  return '📱'; // Default fallback
}

/**
 * Debug function to inspect media structure
 */
export function debugMediaStructure(media: any): {
  originalType: string;
  structure: string;
  normalized: NormalizedMedia | null;
  hasUrl: boolean;
  isArray: boolean;
} {
  return {
    originalType: typeof media,
    structure: Array.isArray(media) ? 'array' : 
               media?.mapValue ? 'firestore-mapValue' : 
               typeof media === 'string' ? 'string' : 
               media?.url ? 'object-with-url' : 'unknown',
    normalized: normalizeMedia(media),
    hasUrl: !!(normalizeMedia(media)?.url),
    isArray: Array.isArray(media)
  };
} 